/* PB Footer — reveal on final-section intersection (desktop only)
   1. Sync --pb-footer-reveal to the real footer height so the bottom
      spacer under #MainContent is exactly as tall as the footer.
   2. Toggle `.is-visible` on the footer wrapper when a sentinel placed
      at the end of #MainContent enters the viewport. This is a true
      "near the bottom" detection — independent of last-section height
      (a previous intersectionRatio>=0.2 check failed silently on tall
      collection grids and review-app sections).
      Footer stays hidden during mid-page scroll (prevents overlap with
      GSAP-pinned sections like grid-reveal and vault).
   On ≤1024px the footer is in normal flow; all the JS does is keep the
   state clean when the user resizes across the breakpoint.
*/
(function () {
  'use strict';

  var DESKTOP_MIN = 1025;
  var root = document.documentElement;
  var state = { io: null, resizeObs: null };

  function footerEl() {
    return document.querySelector('.shopify-section-pb-footer');
  }

  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN;
  }

  // PB:MODIFIED 2026-05-12 — footer_mode setting.
  // When inner .pb-footer is marked data-pb-footer-mode="normal", skip the
  // reveal observer and spacer entirely so the footer stays in document flow.
  function isNormalMode() {
    var inner = document.querySelector('.pb-footer[data-pb-footer-mode]');
    return inner && inner.getAttribute('data-pb-footer-mode') === 'normal';
  }

  function syncHeight() {
    var el = footerEl();
    if (!el) return;
    if (isDesktop() && !isNormalMode()) {
      var h = el.offsetHeight;
      if (h > 0) root.style.setProperty('--pb-footer-reveal', h + 'px');
    } else {
      root.style.removeProperty('--pb-footer-reveal');
    }
  }

  function setVisible(el, on) {
    if (!el) return;
    if (on) el.classList.add('is-visible');
    else el.classList.remove('is-visible');
  }

  function ensureSentinel() {
    var main = document.getElementById('MainContent');
    if (!main) return null;
    var sentinel = main.querySelector(':scope > .pb-footer-sentinel');
    if (sentinel) return sentinel;
    sentinel = document.createElement('div');
    sentinel.className = 'pb-footer-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'display:block;width:100%;height:1px;pointer-events:none;';
    main.appendChild(sentinel);
    return sentinel;
  }

  function teardown() {
    if (state.io) { state.io.disconnect(); state.io = null; }
    if (state.resizeObs) { state.resizeObs.disconnect(); state.resizeObs = null; }
  }

  function setup() {
    teardown();
    var footer = footerEl();
    if (!footer) return;

    if (isNormalMode()) {
      // PB:MODIFIED 2026-05-12 — Normal mode: footer is in standard flow at all
      // breakpoints. Clear reveal class and spacer var; CSS handles the rest.
      setVisible(footer, false);
      root.style.removeProperty('--pb-footer-reveal');
      return;
    }

    if (!isDesktop()) {
      // Mobile: footer is in normal flow; make sure class is cleared.
      setVisible(footer, false);
      return;
    }

    syncHeight();

    // Observe footer size so the reveal spacer stays correct if content reflows.
    if ('ResizeObserver' in window) {
      state.resizeObs = new ResizeObserver(syncHeight);
      state.resizeObs.observe(footer);
    }

    var sentinel = ensureSentinel();
    if (!sentinel) {
      setVisible(footer, true);
      return;
    }

    // Reveal the footer when the sentinel (placed at the very end of
    // #MainContent, just before the footer-reveal margin) is within the
    // viewport. This is true regardless of how tall any single section is.
    // The small bottom rootMargin lets the reveal start a hair before the
    // sentinel actually crosses the viewport edge, so the fade feels natural.
    state.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        setVisible(footer, entry.isIntersecting);
      });
    }, {
      threshold: 0,
      rootMargin: '0px 0px 10% 0px'
    });
    state.io.observe(sentinel);
  }

  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(setup, 120);
  }

  function init() {
    setup();
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('load', syncHeight);
    document.addEventListener('shopify:section:load', setup);
    document.addEventListener('shopify:section:unload', setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
